import { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from "react-router-dom";

import ComplaintUploadWithImagePreviewList from "./child/ComplaintUploadWithImagePreviewList";

const ComplainantFeedbackForm = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const { complaintId } = useParams();
  const [uploadedEvidenceUrls, setUploadedEvidenceUrls] = useState([]);
  const [formData, setFormData] = useState({
    complainant_name: "",
    mobile_number: "",
    complaint_number: complaintId || "",
    remarks: "",
    rating: 0,
  });

  useEffect(() => {
    if (complaintId) {
      setFormData((prev) => ({ ...prev, complaint_number: complaintId }));
    }
  }, [complaintId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRating = (ratingValue) => {
    setFormData({ ...formData, rating: ratingValue });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "mobile_number" && value) {
        const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
        if (!pakPhoneRegex.test(value)) {
            toast.error("Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)");
        }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.complainant_name || !formData.mobile_number || !formData.complaint_number) {
        toast.error("Please fill in all required fields.");
        return;
    }

    if (formData.rating === 0) {
        toast.error("Please provide a rating.");
        return;
    }

    const apiData = {
      complaint_number: formData.complaint_number,
      rating: formData.rating,
      remarks: formData.remarks,
      complainant_name: formData.complainant_name,
      complainant_mobile_no: formData.mobile_number,
      evidence_url: uploadedEvidenceUrls.length > 0 ? uploadedEvidenceUrls[uploadedEvidenceUrls.length - 1] : ""
    };

    try {
        const response = await axios.post(`${API_BASE_URL}/backend/complaints/create-complaint-review`, apiData, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        if (response.data) {
             toast.success("Feedback submitted successfully!");
             setFormData({
                complainant_name: "",
                mobile_number: "",
                complaint_number: "",
                remarks: "",
                rating: 0,
             });
             setUploadedEvidenceUrls([]); 
        }
    } catch (error) {
        console.error("Error submitting feedback:", error);
        toast.error(error.response?.data?.message || "Failed to submit feedback. Please try again.");
    }
  };

  return (
    <div className="complaint-form col-lg-12 col-md-12 col-sm-12">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-900 to-emerald-800 p-8 shadow-2xl shadow-emerald-900/40">
              <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-blue-400/10 blur-2xl"></div>
              <div className="relative z-10 text-center">
                <div className="mb-2">
                  <span className="text-sm font-semibold uppercase tracking-widest text-success-300">
                    Department of Forestry & Wildlife
                  </span>
                </div>
                <h1 className="text-5xl font-bold leading-tight text-white">
                  <span className="text-3xl font-medium text-emerald-100 text-success-500">
                    Complainant Feedback Form
                  </span>
                </h1>
                <p className="text-lg italic text-emerald-100/90 text-success-300">
                  "Your feedback helps us improve our services"
                </p>
              </div>
            </div>

            <div className="text-xl text-white font-bold mt-4">Feedback Details</div>
            <hr className="pb-05"/>
            <div className="row gy-3">
              
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-white align-items-center gap-2">
                  <Icon icon="mdi:account" className="text-lg" />
                  <span className="text-md" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>Complainant Name</span>
                </label>
                <input
                  type="text"
                  name="complainant_name"
                  placeholder="Complainant Name"
                  className="form-control"
                  value={formData.complainant_name}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:phone" className="text-lg" />
                  Mobile Number
                </label>
                <input
                  type="text"
                  name="mobile_number"
                  placeholder="Phone Number (e.g., 03001234567)"
                  className="form-control"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-white align-items-center gap-2">
                  <Icon icon="mdi:file-document-outline" className="text-lg" />
                  <span className="text-md" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>Complaint Number</span>
                </label>
                <input
                  type="text"
                  name="complaint_number"
                  placeholder="Complaint Number"
                  className="form-control"
                  value={formData.complaint_number}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:comment-text-outline" className="text-lg" />
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  className="form-control"
                  rows={4}
                  placeholder="Enter your remarks..."
                  value={formData.remarks}
                  onChange={handleInputChange}
                  style={{ backgroundColor: 'var(--white)', height: '130px' }}
                />
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                    <Icon icon="mdi:star" className="text-lg" />
                    Rating
                </label>
                <div 
                  className="form-control d-flex align-items-center justify-content-center gap-3" 
                  style={{ backgroundColor: 'var(--white)', height: '130px', padding: '6px 20px' }}
                >
                    {[1, 2, 3, 4, 5].map((star) => (
                    <Icon
                        key={star}
                        icon={star <= formData.rating ? "mdi:star" : "mdi:star-outline"}
                        className={`cursor-pointer ${star <= formData.rating ? "text-warning" : "text-secondary"}`}
                        width="40"
                        height="40"
                        onClick={() => handleRating(star)}
                        style={{ cursor: 'pointer' }}
                    />
                    ))}
                </div>
              </div>

              <div className="col-lg-12 col-md-12 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:file-upload" className="text-lg" />
                  Evidence if any
                </label>
                <ComplaintUploadWithImagePreviewList
                  onFileUploadSuccess={(url) => {
                    setUploadedEvidenceUrls(prev => [...prev, url]);
                  }}
                />
                <label className="text-sm mt-1 mb-0 text-white">Upload up to 5 supported files of Pdf, word or Jpg format (Max 5 MB)</label>
              </div>

            </div>
            
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-success text-sm btn-sm px-6 py-12 radius-12 my-4 mx-10 col-lg-2 col-md-4 col-sm-12"
                type="submit"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplainantFeedbackForm;